/**
 * Cart Shipping Notification Dismissal Handler
 *
 * Requirements:
 * - REQ-11: Dismissible close button
 * - REQ-12: Session persistence via sessionStorage
 * - REQ-13: Reappears on new sessions
 * - REQ-14: Accessible (keyboard navigation, screen reader support)
 * - Zero CLS when dismissing
 */

class CartShippingNotification {
  constructor() {
    this.STORAGE_KEY = 'shipping-notification-dismissed';
    this.notification = null;
    this.closeButton = null;
    this.init();
  }

  init() {
    // Find elements
    this.notification = document.querySelector('[data-free-shipping-notification]');
    this.closeButton = document.querySelector('[data-shipping-close]');

    if (!this.notification || !this.closeButton) {
      return; // Elements not found, exit gracefully
    }

    // Check if notification was previously dismissed in this session
    if (this.isDismissed()) {
      this.hideNotification(false); // Hide without animation on page load
      return;
    }

    // Bind close button event listeners
    this.bindEvents();
  }

  bindEvents() {
    if (!this.closeButton) return;

    // Click event
    this.closeButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.dismissNotification();
    });

    // Keyboard accessibility - Enter and Space keys
    this.closeButton.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        e.stopPropagation();
        this.dismissNotification();
      }
    });
  }

  dismissNotification() {
    // Save dismissal state to sessionStorage
    this.saveDismissalState();

    // Hide notification with smooth animation
    this.hideNotification(true);
  }

  hideNotification(animate = true) {
    if (!this.notification) return;

    if (animate) {
      // Add transition for smooth dismissal
      this.notification.style.transition = 'opacity 0.2s ease, transform 0.2s ease';
      this.notification.style.opacity = '0';
      this.notification.style.transform = 'translateY(-10px)';

      // After animation completes, hide completely
      setTimeout(() => {
        this.notification.style.display = 'none';
        // Reset parent container height to prevent layout shift
        const container = document.getElementById('cart-shipping-notification');
        if (container) {
          container.style.display = 'none';
        }
      }, 200);
    } else {
      // Instant hide (for page load when previously dismissed)
      this.notification.style.display = 'none';
      const container = document.getElementById('cart-shipping-notification');
      if (container) {
        container.style.display = 'none';
      }
    }
  }

  saveDismissalState() {
    try {
      sessionStorage.setItem(this.STORAGE_KEY, 'true');
    } catch (e) {
      // SessionStorage might be disabled or full
      console.warn('Could not save shipping notification dismissal state:', e);
    }
  }

  isDismissed() {
    try {
      return sessionStorage.getItem(this.STORAGE_KEY) === 'true';
    } catch (e) {
      // SessionStorage might be disabled
      return false;
    }
  }

  // Public method to reinitialize after AJAX cart updates
  reinitialize() {
    // Re-query elements as they may have been replaced by AJAX
    this.notification = document.querySelector('[data-free-shipping-notification]');
    this.closeButton = document.querySelector('[data-shipping-close]');

    // If notification was previously dismissed, keep it hidden
    if (this.isDismissed() && this.notification) {
      this.hideNotification(false);
    } else if (this.notification && this.closeButton) {
      // Re-bind events if elements exist and notification wasn't dismissed
      this.bindEvents();
    }
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.cartShippingNotification = new CartShippingNotification();
  });
} else {
  // DOM is already loaded
  window.cartShippingNotification = new CartShippingNotification();
}

// Re-initialize after cart AJAX updates
document.addEventListener('cart:updated', () => {
  if (window.cartShippingNotification) {
    window.cartShippingNotification.reinitialize();
  }
});

// Re-initialize after section rendering (Shopify Section Rendering API)
document.addEventListener('shopify:section:load', (e) => {
  if (e.detail.sectionId && e.detail.sectionId.includes('cart-shipping-notification')) {
    if (window.cartShippingNotification) {
      window.cartShippingNotification.reinitialize();
    }
  }
});

// Export for testing or external use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CartShippingNotification;
}